import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, IconButton, Divider, Switch, FormControlLabel, Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { MOON_PEAK_DEFAULT } from '../../constants/moon';

const OBS_TYPES = [
  { value: 'flexible', label: 'Flexible' },
  { value: 'time_based', label: 'Time-Based' },
  { value: 'fixed_time', label: 'Fixed Time' },
  { value: 'rise_to_set', label: 'Rise to Set' },
];

const TemplateForm = ({ open, onClose, onSave, template, projects, observatories, libraryTargets, organizations }) => {
  const { user, updateUserPreferences } = useAuth();
  const isEdit = !!template;

  const [form, setForm] = useState(() => ({
    name: template?.name || '',
    description: template?.description || '',
    project_id: template?.project_id?._id || template?.project_id || '',
    observatory_id: template?.observatory_id?._id || template?.observatory_id
      || ((user?.preferences?.last_observatory_id && observatories?.find(o => o._id === user.preferences.last_observatory_id)) ? user.preferences.last_observatory_id : ''),
    visibility: template?.visibility || 'private',
    organization_id: template?.organization_id?._id || template?.organization_id || '',
    observation_type: template?.observation_type || 'flexible',
    priority: template?.priority || 5,
    global_repeat_count: template?.global_repeat_count || 1,
    targets: template?.targets?.length > 0
      ? template.targets.map(t => ({
          ...t,
          ra: t.ra?.toString() || '',
          dec: t.dec?.toString() || '',
          exposure_configs: t.exposure_configs?.length > 0 ? t.exposure_configs.map(ec => ({ ...ec })) : [],
          constraints: t.constraints || { minimum_altitude: 30, maximum_airmass: 2.0, moon_separation: MOON_PEAK_DEFAULT, twilight_type: 'nautical' }
        }))
      : [],
    timing: {
      min_time_minutes: template?.timing?.min_time_minutes || '',
      desired_time_minutes: template?.timing?.desired_time_minutes || '',
      fill_time: template?.timing?.fill_time || false,
      safety_margin_minutes: template?.timing?.safety_margin_minutes || 15,
    },
    guiding_options: {
      enabled: template?.guiding_options?.enabled ?? true,
      dither_enabled: template?.guiding_options?.dither_enabled ?? false,
    },
    autofocus_options: {
      initial_autofocus: template?.autofocus_options?.initial_autofocus ?? true,
      interval_minutes: template?.autofocus_options?.interval_minutes ?? 30,
    },
    automation: {
      enabled: template?.automation?.enabled || false,
      interval_days: template?.automation?.interval_days || 1,
      interval_hours: template?.automation?.interval_hours || 0,
      interval_minutes: template?.automation?.interval_minutes || 0,
      max_executions: template?.automation?.max_executions || '',
      start_date: template?.automation?.start_date ? new Date(template.automation.start_date).toISOString().slice(0, 16) : '',
      end_date: template?.automation?.end_date ? new Date(template.automation.end_date).toISOString().slice(0, 16) : '',
    },
  }));

  // Derive available filters from selected observatory
  const selectedObservatory = useMemo(() => {
    if (!form.observatory_id || !observatories) return null;
    return observatories.find(o => o._id === form.observatory_id);
  }, [form.observatory_id, observatories]);

  const filters = useMemo(() => {
    const obsFilters = selectedObservatory?.capabilities?.available_filters;
    if (obsFilters && obsFilters.length > 0) return obsFilters;
    if (selectedObservatory) return ['NoFilter'];
    return [];
  }, [selectedObservatory]);

  const makeDefaultExposure = () => ({
    filter: filters[0] || 'L',
    exposure_time: 300,
    exposure_count: 1,
    binning: 1,
    gain: null,
    offset: null
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleObservatoryChange = (newObsId) => {
    if (newObsId) {
      updateUserPreferences({ last_observatory_id: newObsId });
    }
    const newObs = observatories?.find(o => o._id === newObsId);
    const newFilters = newObs?.capabilities?.available_filters;
    const validFilters = (newFilters && newFilters.length > 0) ? newFilters : (newObs ? ['NoFilter'] : []);

    setForm(prev => {
      const updatedTargets = prev.targets.map(t => ({
        ...t,
        exposure_configs: t.exposure_configs.map(ec => ({
          ...ec,
          filter: validFilters.includes(ec.filter) ? ec.filter : (validFilters[0] || ec.filter)
        }))
      }));
      return { ...prev, observatory_id: newObsId, targets: updatedTargets };
    });
  };

  const handleNestedChange = (group, field, value) => {
    setForm(prev => ({
      ...prev,
      [group]: { ...prev[group], [field]: value }
    }));
  };

  const handleTargetChange = (tIdx, field, value) => {
    setForm(prev => {
      const targets = [...prev.targets];
      targets[tIdx] = { ...targets[tIdx], [field]: value };
      return { ...prev, targets };
    });
  };

  const handleTargetExposureChange = (tIdx, eIdx, field, value) => {
    setForm(prev => {
      const targets = [...prev.targets];
      const configs = [...targets[tIdx].exposure_configs];
      configs[eIdx] = { ...configs[eIdx], [field]: value };
      targets[tIdx] = { ...targets[tIdx], exposure_configs: configs };
      return { ...prev, targets };
    });
  };

  const addTargetExposure = (tIdx) => {
    setForm(prev => {
      const targets = [...prev.targets];
      targets[tIdx] = {
        ...targets[tIdx],
        exposure_configs: [...targets[tIdx].exposure_configs, makeDefaultExposure()]
      };
      return { ...prev, targets };
    });
  };

  const removeTargetExposure = (tIdx, eIdx) => {
    setForm(prev => {
      const targets = [...prev.targets];
      targets[tIdx] = {
        ...targets[tIdx],
        exposure_configs: targets[tIdx].exposure_configs.filter((_, i) => i !== eIdx)
      };
      return { ...prev, targets };
    });
  };

  const addTarget = () => {
    setForm(prev => ({
      ...prev,
      targets: [...prev.targets, {
        name: '', ra: '', dec: '', epoch: 'J2000', rotation: 0,
        priority: 5, sequence_repeat_count: 1,
        exposure_configs: form.observatory_id ? [makeDefaultExposure()] : [],
        constraints: { minimum_altitude: 30, maximum_airmass: 2.0, moon_separation: MOON_PEAK_DEFAULT, twilight_type: 'nautical' }
      }]
    }));
  };

  const addFromLibrary = (libraryTarget) => {
    const t = {
      library_target_id: libraryTarget._id,
      name: libraryTarget.name,
      ra: libraryTarget.ra.toString(),
      dec: libraryTarget.dec.toString(),
      epoch: libraryTarget.epoch || 'J2000',
      rotation: libraryTarget.rotation || 0,
      priority: 5,
      sequence_repeat_count: 1,
      exposure_configs: form.observatory_id ? [makeDefaultExposure()] : [],
      constraints: libraryTarget.constraints || { minimum_altitude: 30, maximum_airmass: 2.0, moon_separation: MOON_PEAK_DEFAULT, twilight_type: 'nautical' }
    };
    setForm(prev => ({
      ...prev,
      targets: [...prev.targets, t]
    }));
  };

  const removeTarget = (tIdx) => {
    setForm(prev => ({
      ...prev,
      targets: prev.targets.filter((_, i) => i !== tIdx)
    }));
  };

  const handleSubmit = () => {
    const data = {
      name: form.name,
      description: form.description,
      project_id: form.project_id,
      observatory_id: form.observatory_id,
      visibility: form.visibility || 'private',
      organization_id: form.visibility === 'organization' ? form.organization_id : undefined,
      observation_type: form.observation_type,
      priority: parseInt(form.priority),
      global_repeat_count: parseInt(form.global_repeat_count) || 1,
      targets: form.targets.map(t => ({
        library_target_id: t.library_target_id || undefined,
        name: t.name,
        ...(t.naif_id ? { naif_id: t.naif_id } : {}),   // #1409: persist spacecraft NAIF id in the template
        ra: parseFloat(t.ra),
        dec: parseFloat(t.dec),
        epoch: t.epoch || 'J2000',
        rotation: parseFloat(t.rotation) || 0,
        priority: parseInt(t.priority) || 5,
        sequence_repeat_count: parseInt(t.sequence_repeat_count) || 1,
        exposure_configs: t.exposure_configs.map(ec => ({
          filter: ec.filter,
          exposure_time: parseFloat(ec.exposure_time),
          exposure_count: parseInt(ec.exposure_count),
          binning: parseInt(ec.binning),
          gain: ec.gain != null && ec.gain !== '' ? parseInt(ec.gain) : undefined,
          offset: ec.offset != null && ec.offset !== '' ? parseInt(ec.offset) : undefined,
        })),
        constraints: t.constraints,
      })),
      timing: form.observation_type === 'time_based' ? {
        min_time_minutes: form.timing.min_time_minutes ? parseInt(form.timing.min_time_minutes) : undefined,
        desired_time_minutes: form.timing.desired_time_minutes ? parseInt(form.timing.desired_time_minutes) : undefined,
        fill_time: form.timing.fill_time,
      } : form.observation_type === 'rise_to_set' ? {
        safety_margin_minutes: parseInt(form.timing.safety_margin_minutes) || 15,
      } : {},
      guiding_options: form.guiding_options,
      autofocus_options: form.autofocus_options,
      automation: form.automation.enabled ? {
        enabled: true,
        status: 'active',
        interval_days: parseFloat(form.automation.interval_days) || 0,
        interval_hours: parseFloat(form.automation.interval_hours) || 0,
        interval_minutes: parseFloat(form.automation.interval_minutes) || 0,
        start_date: form.automation.start_date ? new Date(form.automation.start_date).toISOString() : undefined,
        end_date: form.automation.end_date ? new Date(form.automation.end_date).toISOString() : undefined,
        max_executions: form.automation.max_executions ? parseInt(form.automation.max_executions) : undefined,
      } : { enabled: false, status: 'disabled' },
    };
    onSave(data);
  };

  const hasValidTargets = form.targets.length > 0 && form.targets.every(t => t.name && t.ra && t.dec);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Template' : 'Create Template'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Template basics - name and observatory on first row */}
          <Grid item xs={12} sm={4}>
            <TextField label="Template Name" fullWidth required value={form.name} onChange={e => handleChange('name', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>Observatory *</InputLabel>
              <Select
                value={form.observatory_id}
                onChange={e => handleObservatoryChange(e.target.value)}
                label="Observatory *"
              >
                {(observatories || []).map(o => <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required>
              <InputLabel>Project</InputLabel>
              <Select value={form.project_id} onChange={e => handleChange('project_id', e.target.value)} label="Project">
                {(projects || []).map(p => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Sharing</InputLabel>
              <Select
                value={form.visibility}
                onChange={e => {
                  const v = e.target.value;
                  handleChange('visibility', v);
                  if (v !== 'organization') handleChange('organization_id', '');
                }}
                label="Sharing"
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="observatory">Observatory</MenuItem>
                <MenuItem value="organization">Organization</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {form.visibility === 'organization' && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={form.organization_id}
                  onChange={e => handleChange('organization_id', e.target.value)}
                  label="Organization"
                >
                  {(organizations || []).map(o => (
                    <MenuItem key={o._id || o.organization_id} value={o._id || o.organization_id}>
                      {o.name || o.organization_name || 'Unknown'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Observation Type</InputLabel>
              <Select value={form.observation_type} onChange={e => handleChange('observation_type', e.target.value)} label="Observation Type">
                {OBS_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Priority" fullWidth type="number" value={form.priority} onChange={e => handleChange('priority', e.target.value)} inputProps={{ min: 1, max: 10 }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Global Repeats" fullWidth type="number" value={form.global_repeat_count} onChange={e => handleChange('global_repeat_count', e.target.value)} inputProps={{ min: 1 }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Description" fullWidth multiline rows={2} value={form.description} onChange={e => handleChange('description', e.target.value)} />
          </Grid>

          {/* Time-based timing */}
          {form.observation_type === 'time_based' && (
            <>
              <Grid item xs={4}>
                <TextField label="Min Time (min)" fullWidth type="number" value={form.timing.min_time_minutes} onChange={e => handleNestedChange('timing', 'min_time_minutes', e.target.value)} />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Desired Time (min)" fullWidth type="number" value={form.timing.desired_time_minutes} onChange={e => handleNestedChange('timing', 'desired_time_minutes', e.target.value)} />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={<Switch checked={form.timing.fill_time} onChange={e => handleNestedChange('timing', 'fill_time', e.target.checked)} />}
                  label="Fill Time"
                />
              </Grid>
            </>
          )}
          {form.observation_type === 'rise_to_set' && (
            <Grid item xs={4}>
              <TextField label="Safety Margin (min)" fullWidth type="number" value={form.timing.safety_margin_minutes} onChange={e => handleNestedChange('timing', 'safety_margin_minutes', e.target.value)} />
            </Grid>
          )}

          {/* Targets */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">Targets</Typography>
              <Box>
                <Button size="small" startIcon={<AddIcon />} onClick={addTarget} sx={{ mr: 1 }}>Add Manual</Button>
                {libraryTargets && libraryTargets.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Add from Library</InputLabel>
                    <Select
                      value=""
                      onChange={e => {
                        const lt = libraryTargets.find(t => t._id === e.target.value);
                        if (lt) addFromLibrary(lt);
                      }}
                      label="Add from Library"
                    >
                      {libraryTargets.map(lt => <MenuItem key={lt._id} value={lt._id}>{lt.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Box>

            {!form.observatory_id && form.targets.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Select an observatory above to configure exposure filters for your targets.
              </Alert>
            )}

            {form.targets.map((target, tIdx) => (
              <Box key={tIdx} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Target {tIdx + 1}{target.library_target_id ? ' (from library)' : ''}</Typography>
                  <IconButton size="small" onClick={() => removeTarget(tIdx)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <TextField size="small" label="Name" fullWidth required value={target.name} onChange={e => handleTargetChange(tIdx, 'name', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField size="small" label="RA (deg)" fullWidth required type="number" value={target.ra} onChange={e => handleTargetChange(tIdx, 'ra', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField size="small" label="Dec (deg)" fullWidth required type="number" value={target.dec} onChange={e => handleTargetChange(tIdx, 'dec', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} sm={1}>
                    <TextField size="small" label="Pri" type="number" fullWidth value={target.priority} onChange={e => handleTargetChange(tIdx, 'priority', e.target.value)} inputProps={{ min: 1, max: 10 }} />
                  </Grid>
                  <Grid item xs={6} sm={1}>
                    <TextField size="small" label="Rpt" type="number" fullWidth value={target.sequence_repeat_count} onChange={e => handleTargetChange(tIdx, 'sequence_repeat_count', e.target.value)} inputProps={{ min: 1 }} />
                  </Grid>
                </Grid>

                {/* Per-target exposures - only when observatory is selected */}
                {form.observatory_id ? (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="caption">Exposures</Typography>
                      <Button size="small" onClick={() => addTargetExposure(tIdx)}>+ Filter</Button>
                    </Box>
                    {target.exposure_configs.map((ec, eIdx) => (
                      <Box key={eIdx} sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 70 }}>
                          <Select value={ec.filter} onChange={e => handleTargetExposureChange(tIdx, eIdx, 'filter', e.target.value)}>
                            {filters.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                          </Select>
                        </FormControl>
                        <TextField size="small" label="Time" type="number" sx={{ width: 80 }} value={ec.exposure_time} onChange={e => handleTargetExposureChange(tIdx, eIdx, 'exposure_time', e.target.value)} />
                        <TextField size="small" label="Cnt" type="number" sx={{ width: 65 }} value={ec.exposure_count} onChange={e => handleTargetExposureChange(tIdx, eIdx, 'exposure_count', e.target.value)} />
                        <FormControl size="small" sx={{ minWidth: 55 }}>
                          <Select value={ec.binning} onChange={e => handleTargetExposureChange(tIdx, eIdx, 'binning', e.target.value)}>
                            {[1, 2, 3, 4].map(b => <MenuItem key={b} value={b}>{b}x{b}</MenuItem>)}
                          </Select>
                        </FormControl>
                        <TextField size="small" label="Gain" type="number" sx={{ width: 70 }} value={ec.gain ?? ''} onChange={e => handleTargetExposureChange(tIdx, eIdx, 'gain', e.target.value)} inputProps={{ min: 0 }} />
                        <TextField size="small" label="Offset" type="number" sx={{ width: 70 }} value={ec.offset ?? ''} onChange={e => handleTargetExposureChange(tIdx, eIdx, 'offset', e.target.value)} inputProps={{ min: 0 }} />
                        {target.exposure_configs.length > 1 && (
                          <IconButton size="small" onClick={() => removeTargetExposure(tIdx, eIdx)}><DeleteIcon fontSize="small" /></IconButton>
                        )}
                      </Box>
                    ))}
                    {target.exposure_configs.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        No exposures configured. Click "+ Filter" to add one.
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Select an observatory above to configure filters.
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Grid>

          {/* Automation */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={<Switch checked={form.automation.enabled} onChange={e => handleNestedChange('automation', 'enabled', e.target.checked)} />}
              label={<Typography variant="subtitle1">Enable Automation</Typography>}
            />
          </Grid>
          {form.automation.enabled && (
            <>
              <Grid item xs={3}>
                <TextField label="Interval (days)" fullWidth type="number" value={form.automation.interval_days} onChange={e => handleNestedChange('automation', 'interval_days', e.target.value)} inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={3}>
                <TextField label="Interval (hours)" fullWidth type="number" value={form.automation.interval_hours} onChange={e => handleNestedChange('automation', 'interval_hours', e.target.value)} inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={3}>
                <TextField label="Interval (minutes)" fullWidth type="number" value={form.automation.interval_minutes} onChange={e => handleNestedChange('automation', 'interval_minutes', e.target.value)} inputProps={{ min: 0 }} />
              </Grid>
              <Grid item xs={3}>
                <TextField label="Max Executions" fullWidth type="number" value={form.automation.max_executions} onChange={e => handleNestedChange('automation', 'max_executions', e.target.value)} helperText="Leave blank for unlimited" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Start Date" fullWidth type="datetime-local" value={form.automation.start_date} onChange={e => handleNestedChange('automation', 'start_date', e.target.value)} InputLabelProps={{ shrink: true }} helperText="Optional: automation won't start until this date" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="End Date" fullWidth type="datetime-local" value={form.automation.end_date} onChange={e => handleNestedChange('automation', 'end_date', e.target.value)} InputLabelProps={{ shrink: true }} helperText="Optional: automation stops after this date" />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!form.name || !form.project_id || !form.observatory_id || !hasValidTargets}>
          {isEdit ? 'Save Changes' : 'Create Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateForm;
